import React from 'react'
import {Box, Button, Stack, Typography} from '@mui/material'
import WbOutlineButton from '../../components/common/WbOutlineButton'
import BorderLinearProgress from './BorderLinearProgress'
import ContentScreenRender from '../../components/screens/surveyForms/items/ContentScreenRender'
import renderSurveyQuestion from './renderSurveyQuestion'
import {isContentScreen} from '../../utils/surveyItems'
import {advance, canGoBack, currentIndex, goBack, isLastShown} from './runnerNavigation'
import {makeRouteResolver, routeShownIndices} from './routeResolver'

// Steps through the survey the way a respondent would, but writes nothing: it is
// the preview's player. It reuses the shared question renderers and the same
// visited-stack navigation and route resolver as the live runner, so the path it
// takes is exactly the one the real survey would take for these answers.
//
// Navigation is controlled by the container (visited + done), so the container
// can also jump straight to an item (bypassing routing) for the item list.
// Answers live in the container too, so the route-trace panel can read them.
//
// items are preview items (toPreviewItems): evaluator-shaped, carrying the render
// fields. answerMap is the container's current {itemId: {values|number}} map.
export default function PreviewRunner({
  items = [],
  facts = {},
  answerMap = {},
  audience,
  language = 'en',
  onAnswer,
  onRestart,
  visited = [0],
  onVisited,
  done = false,
  onDone,
}) {
  const resolveNext = makeRouteResolver(facts)
  const sel = currentIndex(visited)
  const current = items[sel]

  const restart = () => {
    if (onVisited) onVisited([0])
    if (onDone) onDone(false)
    if (onRestart) onRestart()
  }

  if (done || !current) {
    return (
      <Box className="preview-end" sx={{p: 4, textAlign: 'center'}}>
        <Typography variant="h6" sx={{mb: 1}}>
          End of preview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          This is where the respondent would submit. Nothing was saved.
        </Typography>
        <WbOutlineButton CustomOutlineButtonText="Start again" onClick={restart} />
      </Box>
    )
  }

  const last = isLastShown(visited, items, resolveNext, answerMap)
  const goNext = () => {
    if (last) {
      if (onDone) onDone(true)
      return
    }
    if (onVisited) onVisited(v => advance(v, items, resolveNext, answerMap))
  }

  // Progress over the path actually shown for these answers, the same way the
  // live runner measures it, so the bar fills identically.
  const shown = routeShownIndices(items, answerMap, facts)
  const progress = shown.length ? (Math.max(shown.indexOf(sel), 0) * 100) / shown.length : 0

  // Rendered inside the live survey's own class shell (survey-preview >
  // question-preview > preview > question-box) so the respondent CSS - the 24px
  // question heading, centred layout, option styling - applies unchanged. The
  // preview-runner / --embedded modifiers only unset the full-viewport height
  // and fixed nav that the shell uses on the real screen, so it flows inside the
  // preview dialog instead.
  return (
    <Box className="survey-preview preview-runner">
      <div className="question-preview">
        <div className="preview preview--embedded">
          {/* Keyed by item id so each item mounts a fresh widget: the answer
              renderers seed their questionId and selection from props at mount
              only, so reusing one instance across two same-type questions would
              show a stale selection and record under the previous item's id. */}
          <div className="question-box" key={current.id}>
            {isContentScreen(current) ? (
              <ContentScreenRender content={current.content} />
            ) : (
              renderSurveyQuestion(current, {audience, updateAnswers: onAnswer, language})
            )}
          </div>
        </div>
        <div className="bottom-nav bottom-nav--embedded">
          <div className="question-nav">
            <Stack direction="row" justifyContent="space-between">
              <div>
                {canGoBack(visited) && (
                  <WbOutlineButton
                    CustomOutlineButtonText="Back"
                    onClick={() => onVisited && onVisited(goBack)}
                  />
                )}
              </div>
              <Button onClick={goNext}>{last ? 'Finish' : 'Next'}</Button>
            </Stack>
          </div>
          <BorderLinearProgress variant="determinate" value={progress} />
        </div>
      </div>
    </Box>
  )
}
